import { useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import type React from 'react'
import { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import DefaultModal from '../../../../../components/DefaultModal'
import FormInput from '../../../../../components/FormInput'
import MessageBox from '../../../../../components/MessageBox'
import Toast from '../../../../../components/Toast'
import { useInviteMemberMutation } from '../../../../../redux/services/coreApi'

interface AddMemberModalProps {
  isOpen: boolean
  onClose: () => void
}

type AddMemberFormData = {
  firstName: string
  lastName: string
  phone: string
  email: string
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({ isOpen, onClose }) => {
  const toast = useToast()
  const [inviteMember, { isLoading }] = useInviteMemberMutation()
  const [error, setError] = useState<string | null>(null)

  const schema: yup.SchemaOf<AddMemberFormData> = yup.object({
    firstName: yup.string().required('This field is required.'),
    lastName: yup.string().required('This field is required.'),
    phone: yup.string().required('This field is required.'),
    email: yup
      .string()
      .email('Please enter a valid email.')
      .required('This field is required.'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    watch,
  } = useForm<AddMemberFormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  })

  const resetModal = () => {
    setValue('email', '')
    setValue('firstName', '')
    setValue('lastName', '')
    setValue('phone', '')
    clearErrors()
    setError(null)
    onClose()
  }

  const onSubmit: SubmitHandler<AddMemberFormData> = async ({
    firstName,
    lastName,
    phone,
    email,
  }) => {
    setError(null)
    try {
      await inviteMember({
        firstName,
        lastName,
        phone,
        email,
      }).unwrap()

      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="success">{`${firstName} has been successfully added as a Member of your organization.`}</Toast>
        ),
      })
    } catch (err) {
      let errMsg = (err as any)?.data?.errors
      if (!errMsg)
        errMsg = `There was an error trying to add ${firstName} as a Member of your Organization.`

      toast({
        position: 'bottom-right',
        render: () => <Toast type="error">{errMsg}</Toast>,
      })
      //@ts-ignore
      setError('Something went wrong. Please contact support.')
      return
    }

    resetModal()
  }

  const title = 'Invite account user by email'

  return (
    <DefaultModal
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={() => {
        resetModal()
      }}
      title={title}
      isLoadingText={'Inviting account user'}
      whiteBG
    >
      <form onSubmit={handleSubmit(onSubmit)} className="">
        <div className="p-5 pb-0 space-y-3">
          <FormInput
            label="First name"
            inputMode="text"
            registeredForm={register('firstName')}
            error={errors?.firstName}
          />

          <FormInput
            label="Last name"
            inputMode="text"
            registeredForm={register('lastName')}
            error={errors?.lastName}
          />

          <FormInput
            label="Email"
            inputMode="email"
            registeredForm={register('email')}
            error={errors?.email}
          />

          <FormInput
            label="Phone nr."
            inputMode="text"
            registeredForm={register('phone')}
            error={errors?.phone}
          />
        </div>

        <div className="p-5 pt-0">
          <button type="submit" className="button mt-10">
            Send invite
          </button>
        </div>
      </form>

      <div className="p-5 pt-0">
        {error && <MessageBox type="warning" title={error} />}
      </div>
    </DefaultModal>
  )
}

export default AddMemberModal
